import dayjs from 'dayjs';
import { isNil } from 'lodash';

import { SHORT_MONTH_DAY_YEAR_FORMAT } from 'constants/date';

import { HOURS_PER_DAY } from './CommandCenterHome.constants';
import {
  FILE_QUALITY_AS_OF_PREFIX,
  FILE_QUALITY_WINDOW_PREFIX,
  FILE_QUALITY_WINDOW_SUFFIX,
  QUALITY_METRICS,
  QUALITY_SAMPLE_PREFIX,
  QualityMetricFormat,
  QualityMetricKey,
} from './fileQualityScorecard.constants';

const PERCENT_SUFFIX = '%';
const DAYS_DECIMAL_PLACES = 1;

const METRIC_KEYS = Object.values(QualityMetricKey);

// Prefers the first non-null value across the two halves for a single key. Each
// service nulls the keys it does not own, so the owning service's value wins;
// when neither owns it the merged value stays null.
const mergeMetricKey = (key, underwritingMap, loansMap) => {
  const underwritingValue = underwritingMap?.[key];

  return isNil(underwritingValue) ? (loansMap?.[key] ?? null) : underwritingValue;
};

// Merges a metrics-or-sampleSizes map across both halves, key by key.
const mergeMetricMap = (underwritingMap, loansMap) =>
  METRIC_KEYS.reduce(
    (acc, key) => ({ ...acc, [key]: mergeMetricKey(key, underwritingMap, loansMap) }),
    {},
  );

// Prefers the first non-null scalar (asOf / windowDays / evaluatedAgainst).
const mergeScalar = (underwritingValue, loansValue) =>
  isNil(underwritingValue) ? (loansValue ?? null) : underwritingValue;

const concatMetricFlags = (underwritingFlags, loansFlags) => [
  ...(underwritingFlags ?? []),
  ...(loansFlags ?? []),
];

/**
 * Merges the two scorecard halves into one display payload. Per the ruling:
 * prefer the non-null value per metric key; OR the programMixNote; concat
 * metricFlags. A half that is null (404 / flag off / error) contributes nothing.
 *
 * @param {{ underwriting: object|null, loans: object|null }} params
 * @returns {object|null} merged scorecard, or null when both halves are empty
 */
export const mergeScorecardHalves = ({ underwriting = null, loans = null } = {}) => {
  if (isNil(underwriting) && isNil(loans)) {
    return null;
  }

  return {
    metrics: mergeMetricMap(underwriting?.metrics, loans?.metrics),
    sampleSizes: mergeMetricMap(underwriting?.sampleSizes, loans?.sampleSizes),
    programMixNote: !!underwriting?.programMixNote || !!loans?.programMixNote,
    windowDays: mergeScalar(underwriting?.windowDays, loans?.windowDays),
    asOf: mergeScalar(underwriting?.asOf, loans?.asOf),
    evaluatedAgainst: mergeScalar(underwriting?.evaluatedAgainst, loans?.evaluatedAgainst),
    metricFlags: concatMetricFlags(underwriting?.metricFlags, loans?.metricFlags),
  };
};

// Formats a percentage value (already 0..100 from the backend) as "84%".
const formatPercent = value => `${value}${PERCENT_SUFFIX}`;

/**
 * Formats a median duration in hours into a clean human label: "55h" under a
 * day, "~2.3 days" at a day or more (dropping a trailing ".0"). Mirrors the
 * turn-time-actuals formatter so durations read consistently across the page.
 *
 * @param {number} hours
 * @returns {string}
 */
const formatDurationHours = hours => {
  if (hours < HOURS_PER_DAY) {
    return `${hours}h`;
  }

  const days = Number((hours / HOURS_PER_DAY).toFixed(DAYS_DECIMAL_PLACES));

  return `~${days} days`;
};

// Renders a metric's raw value according to its format. Callers only invoke this
// once they have confirmed the value is non-null.
const formatMetricValue = ({ value, format }) =>
  format === QualityMetricFormat.PERCENT ? formatPercent(value) : formatDurationHours(value);

const getSampleLabel = sampleSize => `${QUALITY_SAMPLE_PREFIX} ${sampleSize}`;

// Per-metric display state. A not-tracked metric is always "not tracked"
// regardless of its (null) value. Otherwise a null value means the broker is
// below the backend's minimum-sample floor → "insufficient data". A present
// value renders with its sample N.
export const MetricDisplayState = {
  VALUE: 'VALUE',
  INSUFFICIENT_DATA: 'INSUFFICIENT_DATA',
  NOT_TRACKED: 'NOT_TRACKED',
};

/**
 * Resolves the display state + formatted value/sample for one metric definition
 * against the merged scorecard.
 *
 * @param {{ metric: object, scorecard: object }} params
 * @returns {{ key, label, format, state, formattedValue: string|null, sampleLabel: string|null }}
 */
const resolveMetricDisplay = ({ metric, scorecard }) => {
  const value = scorecard?.metrics?.[metric.key] ?? null;
  const sampleSize = scorecard?.sampleSizes?.[metric.key] ?? null;

  const baseDisplay = {
    key: metric.key,
    label: metric.label,
    format: metric.format,
    formattedValue: null,
    sampleLabel: null,
  };

  if (metric.isNotTracked) {
    return { ...baseDisplay, state: MetricDisplayState.NOT_TRACKED };
  }

  if (isNil(value)) {
    return { ...baseDisplay, state: MetricDisplayState.INSUFFICIENT_DATA };
  }

  return {
    ...baseDisplay,
    state: MetricDisplayState.VALUE,
    formattedValue: formatMetricValue({ value, format: metric.format }),
    sampleLabel: isNil(sampleSize) ? null : getSampleLabel(sampleSize),
  };
};

export const getMetricDisplays = scorecard =>
  QUALITY_METRICS.map(metric => resolveMetricDisplay({ metric, scorecard }));

// "as of Jun 16, 2026" from the backend's real ISO timestamp; null when absent
// (never a fabricated date).
export const getScorecardAsOfLabel = asOf =>
  isNil(asOf)
    ? null
    : `${FILE_QUALITY_AS_OF_PREFIX} ${dayjs(asOf).format(SHORT_MONTH_DAY_YEAR_FORMAT)}`;

// "Rolling 90 days" from the backend windowDays; null when absent.
export const getScorecardWindowLabel = windowDays =>
  isNil(windowDays)
    ? null
    : `${FILE_QUALITY_WINDOW_PREFIX} ${windowDays} ${FILE_QUALITY_WINDOW_SUFFIX}`;
